import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import Input from "../../../../../components/FormikForm/Input";
import TextArea from "../../../../../components/FormikForm/TextArea";
import Submit from "../../../../../components/FormikForm/Submit";
import Button from "../../../../../components/Button";
import Radio from "../../../../../components/FormikForm/Radio";
import Switch from "../../../../../components/FormikForm/Switch";
import ReactSelect from "../../../../../components/FormikForm/ReactSelect";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../../../components/Tabs";
import { Progress } from "../../../../../components/Progress";
import {
  Label,
  ExplainText,
  HelperLine,
  ErrorMessage,
} from "../../../../../components/FormikForm/Field";
import {
  Flex,
  Steps,
  Container,
  PreviewContainer,
  PreviewDetail,
  PreviewDetailTitle,
  PewviewDetailsDescription,
  Circle,
} from "./styles";
import Icon from "../../../../../components/Icon";
import padlockUnlockIcon from "../../../../../assets/icons/padlock-unlock.svg";
import padlockLockIcon from "../../../../../assets/icons/padlock-lock.svg";
import userConfigIcon from "../../../../../assets/icons/user-config.svg";
import settingIcon from "../../../../../assets/icons/settings.svg";
import eyeIcon from "../../../../../assets/icons/eye-on.svg";
import arrowFowardIcon from "../../../../../assets/icons/arrow-foward.svg";
import { useWindowSize } from "../../../../../hooks/useWindowSize";

const durations = [
  { value: "day", label: "Días" },
  { value: "week", label: "Semanas" },
  { value: "month", label: "Meses" },
  { value: "year", label: "Años" },
];

const translateUnits = (cantidad: number, unidad: "day" | "week" | "month" | "year"): string => {
  const unidades = {
    day: ["día", "días"],
    week: ["semana", "semanas"],
    month: ["mes", "meses"],
    year: ["año", "años"],
  };

  const formas = unidades[unidad];
  if (!formas) return unidad; // fallback por si llega una unidad no contemplada

  return cantidad === 1 ? formas[0] : formas[1];
}

const CategoryFormComponent = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  validateForm,
  setTouched,
  validateField,
  initialValues,
  loading,
  onClose,
  formId,
}) => {
  const { isMobile } = useWindowSize()
  const [tab, setTab] = useState("basic");
  const tabs = ["basic", "advanced", "preview"];

  const progressMap = {
    basic: 33,
    advanced: 67,
    preview: 100,
  };
  const progressValue = progressMap[tab] || 0;

  const totalSteps = tabs.length;
  const currentStep = tabs.indexOf(tab) + 1;
  const percent = progressValue;

  const stepFields = {
    basic: [
      "name",
      "description",
      "is_unlockable",
      "number_to_unlock",
      "unit_to_unlock",
      "unlock_message",
    ],
    advanced: ["grace_period_type", "grace_period_in_days"],
    preview: [],
  };

  const next = useCallback(async () => {
    const currentIndex = tabs.indexOf(tab);
    const nextTab = tabs[currentIndex + 1];
    if (!nextTab) return;

    const fieldsToValidate = stepFields[tab] || [];

    await setTouched(
      fieldsToValidate.reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {}),
      true
    );

    const formErrors = await validateForm();

    const hasErrors = fieldsToValidate.some((field) => !!formErrors[field]);

    if (!hasErrors) {
      setTab(nextTab);
    }
  }, [tab, tabs, stepFields, validateForm, setTouched, setTab]);

  const prev = useCallback(() => {
    const currentIndex = tabs.indexOf(tab);
    const prevTab = tabs[currentIndex - 1];

    if (!prevTab) return null;
    setTab(prevTab);
  }, [tab]);

  const cancelBehaviorOptions = [
    {
      label: "Conserva el progreso permanentemente",
      value: "forever",
      explainText:
        "Se tendrá en cuenta el tiempo que estuvo activo en la membresía, incluso si se dio de baja. Cuando vuelva, el contador no se reinicia: su progreso continúa como si nada hubiese pasado.",
    },
    {
      label: "Pierde el progreso",
      value: "none",
      explainText:
        "Si se vuelve a suscribir, deberá esperar nuevamente el tiempo necesario para desbloquear esta categoría.",
    },
    {
      label: "Pierde el progreso después de un período de gracia",
      value: "limited",
      explainText:
        "Si vuelve dentro del plazo que definas, conserva el progreso. Después de ese tiempo, la categoría volverá a estar bloqueada.",
    },
  ];


  const gracePeriodText = useMemo(() => {
    switch (values?.grace_period_type) {
      case "none":
        return "Se resetea el progreso";
      case "limited":
        return `Conserva el progreso si vuelve dentro de los ${values?.grace_period_in_days} ${Number(values?.grace_period_in_days) === 1 ? "día" : "días"}`;
      case "forever":
        return "Conserva el progreso para siempre";
      default:
        return "";
    }
  }, [values]);


  return (
    <>
      <Container>
        <Flex>
          <Steps>
            Paso {currentStep} de {totalSteps}{" "}
          </Steps>
          <Steps>{percent}% Completado</Steps>
        </Flex>
        <div style={{ height: 12 }} />
        <Progress
          value={progressValue}
          max={100}
          style={{ marginBottom: 20 }}
        />
      </Container>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger disabled value="basic">
            <Flex style={{ justifyContent: "center", gap: 6 }}>
              <Icon
                icon={settingIcon}
                size={"15px"}
                color={tab === "basic" ? "#6c5ce7" : "#6b7280"}
              />{" "}
              Básico
            </Flex>
          </TabsTrigger>
          <TabsTrigger disabled value="advanced">
            <Flex style={{ justifyContent: "center", gap: 6 }}>
              <Icon
                icon={userConfigIcon}
                size={"15px"}
                color={tab === "advanced" ? "#6c5ce7" : "#6b7280"}
              />{" "}
              Avanzado
            </Flex>
          </TabsTrigger>
          <TabsTrigger disabled value="preview">
            <Flex style={{ justifyContent: "center", gap: 6 }}>
              <Icon
                icon={eyeIcon}
                size={"15px"}
                color={tab === "preview" ? "#6c5ce7" : "#6b7280"}
              />{" "}
              Vista previa
            </Flex>
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="basic"
          style={{
            overflow: "auto",
            height: `calc(100vh - 252px)`,
          }}
        >
          <div style={{ height: 20 }} />

          <Container>
            <Input
              name="name"
              error={errors["name"]}
              touched={touched["name"]}
              step="any"
              placeholder="Sin espacios"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values["name"]}
              options={{
                label: "Nombre de la categoría",
                helperText:
                  "No debe contener espacios o caracteres especiales.",
                marginBottom: 0,
              }}
            />

            <div style={{ height: 30 }} />

            <TextArea
              name="description"
              error={errors["description"]}
              touched={touched["description"]}
              value={values["description"]}
              step="any"
              placeholder="Ej: Esta categoría es para..."
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
              options={{
                label: "Descripción de la categoría",
                marginBottom: 0,
                minHeight: 70
              }}
            />

            <div style={{ height: 15 }} />

            <Flex style={{ gap: 8 }}>
              <Switch
                name="is_unlockable"
                value={values?.is_unlockable}
                onChange={(event) => {
                  handleChange(event)
                  const unlockable = event.target.checked;

                  if (!unlockable) {
                    setFieldValue("number_to_unlock", null);
                    setFieldValue("unit_to_unlock", null);
                    setFieldValue("unlock_message", null);
                    setFieldValue("grace_period_type", null);
                    setFieldValue("grace_period_in_days", null);
                    setFieldValue("show_before_unlock", null);
                  } else {
                    setFieldValue("number_to_unlock", initialValues?.number_to_unlock);
                    setFieldValue("unit_to_unlock", initialValues?.unit_to_unlock);
                    setFieldValue("unlock_message", initialValues?.unlock_message);
                    setFieldValue("grace_period_type", initialValues?.grace_period_type);
                    setFieldValue("grace_period_in_days", initialValues?.grace_period_in_days);
                    setFieldValue("show_before_unlock", initialValues?.show_before_unlock);

                    if (values.grace_period_type !== 'limited') {
                      setFieldValue("grace_period_in_days", null)
                    }
                  }

                }}
                onBlur={handleBlur}
                items={[
                  {
                    label: (
                      <Label
                        size="md"
                        htmlFor="is_unlockable_1"
                        style={{ marginBottom: 0 }}
                      >
                        ¿Querés que esta categoría se desbloquee más adelante?
                      </Label>
                    ),
                    value: "true",
                    explainText:
                      "Activalo si querés que los suscriptores accedan después de cierto tiempo.",
                    id: "is_unlockable_1",
                  },
                ]}
                options={{ marginBottom: 0, switchPosition: true }}
              />

              {values?.is_unlockable?.includes("true") ? (
                <div
                  style={{
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#CC7C2F",
                    gap: 2,
                    fontWeight: 500,
                    width: 86,
                  }}
                >
                  <Icon icon={padlockLockIcon} size={"14px"} color="#CC7C2F" />
                  Bloqueada
                </div>
              ) : (
                <div
                  style={{
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#4CA154",
                    gap: 2,
                    fontWeight: 500,
                    width: 86,
                  }}
                >
                  <Icon
                    icon={padlockUnlockIcon}
                    size={"14px"}
                    color="#4CA154"
                  />
                  Disponible
                </div>
              )}
            </Flex>

            <div style={{ height: 30 }} />

            {values?.is_unlockable?.includes("true") && (
              <>
                <Label size="md" htmlFor="number_to_unlock">
                  ¿Cuándo se desbloquea?
                </Label>

                <div style={{ height: 8 }} />

                <Flex style={{ gap: 8 }}>
                  <div style={{ maxWidth: 100 }}>
                    <Input
                      name="number_to_unlock"
                      value={values?.number_to_unlock}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={false}
                      touched={false}
                      options={{ marginBottom: 0 }}
                    />
                  </div>

                  <ReactSelect
                    name="unit_to_unlock"
                    error={false}
                    touched={false}
                    items={durations}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={{
                      marginBottom: 0,
                    }}
                  />
                </Flex>

                {(errors?.number_to_unlock && touched?.number_to_unlock) ||
                  (errors?.unit_to_unlock && touched?.unit_to_unlock) ? (
                  <HelperLine marginBottom={0}>
                    <ErrorMessage>
                      {errors?.number_to_unlock &&
                        touched?.number_to_unlock &&
                        errors?.number_to_unlock}
                      {errors?.unit_to_unlock &&
                        touched?.unit_to_unlock &&
                        errors?.unit_to_unlock}
                    </ErrorMessage>
                  </HelperLine>
                ) : (
                  <ExplainText>
                    Los suscriptores podrán acceder después de cumplir el tiempo
                    que elijas.
                  </ExplainText>
                )}

                <div style={{ height: 30 }} />

                <TextArea
                  name="unlock_message"
                  value={values?.unlock_message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors["unlock_message"]}
                  touched={touched["unlock_message"]}
                  placeholder="¡Felicitaciones! Has desbloqueado esta categoría."
                  options={{
                    label: "Mensaje de felicitaciones",
                    helperText:
                      "Este mensaje se mostrará cuando el suscriptor desbloquee esta categoría.",
                    marginBottom: 0,
                  }}
                />

                <div style={{ height: 30 }} />
              </>
            )}
          </Container>

          <div style={{ height: 20 }} />
        </TabsContent>

        <TabsContent
          value="advanced"
          style={{
            overflow: "auto",
            height: `calc(100vh - 252px)`,
          }}
        >
          <div style={{ height: 20 }} />

          <Container>
            {values?.is_unlockable?.includes("true") ? (
              <>
                <Radio
                  name="grace_period_type"
                  error={errors["grace_period_type"]}
                  touched={touched["grace_period_type"]}
                  items={cancelBehaviorOptions}
                  value={values["grace_period_type"]}
                  onChange={(event) => {
                    handleChange(event);
                    if (event.target.value !== 'limited') {
                      setFieldValue("grace_period_in_days", null)
                    }
                  }}
                  onBlur={handleBlur}
                  options={{
                    label:
                      "¿Qué pasa con esta categoría si un suscriptor se da de baja?",
                    helperText:
                      "Elige qué quieres que ocurra con el progreso del suscriptor si cancela su membresía y más adelante vuelve a activarla.",
                    marginBottom: 0,
                    vertical: true,
                  }}
                />

                <div style={{ height: 20 }} />

                {values?.grace_period_type === "limited" && (
                  <Input
                    name="grace_period_in_days"
                    error={errors["grace_period_in_days"]}
                    touched={touched["grace_period_in_days"]}
                    placeholder="Ej: 60 (en días)"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values["grace_period_in_days"]}
                    type="number"
                    options={{
                      label:
                        "¿Cuántos días tiene para volver sin perder esta categoría?",
                      marginBottom: 0,
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <div style={{ height: 50 }} />

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 40,
                  }}
                >
                  <Icon icon={padlockUnlockIcon} size="48px" color="#717171" />
                  <span style={{ fontSize: 18, fontWeight: 500 }}>
                    Esta categoría estará siempre disponible
                  </span>
                  <span style={{ color: "#71717a", textAlign: "center" }}>
                    Las opciones avanzadas solo están disponibles para
                    categorías que requieren desbloqueo. Volvé al paso anterior
                    si querés configurar condiciones de desbloqueo.
                  </span>
                  <Button
                    disabled={loading}
                    options={{
                      type: "outline",
                      size: "lg",
                      skin: "black",
                    }}
                    onClick={() => prev()}
                  >
                    Configurar como categoría bloqueada
                  </Button>
                </div>
              </>
            )}
          </Container>

          <div style={{ height: 20 }} />
        </TabsContent>

        <TabsContent
          value="preview"
          style={{
            overflow: "auto",
            height: `calc(100vh - 252px)`,
          }}
        >
          <div style={{ height: 20 }} />

          <Container>
            <Flex style={{ justifyContent: "flex-start", gap: 8 }}>
              <Icon icon={eyeIcon} size={"20px"} color="#6c5ce7" />
              <span style={{ fontWeight: 600 }}>Resumen de configuración</span>
            </Flex>

            <div style={{ height: 16 }} />

            {values?.is_unlockable?.includes("true") ? (
              <PreviewContainer>
                <Flex style={{ alignItems: "flex-start", gap: 8 }}>
                  <Circle>
                    <Icon
                      icon={padlockLockIcon}
                      size={"15px"}
                      color="#6c5ce7"
                    />
                  </Circle>

                  <div style={{ width: "100%" }}>
                    <PreviewDetail>
                      <PreviewDetailTitle>{values?.name}</PreviewDetailTitle>
                      <PewviewDetailsDescription style={{ color: "#717171" }}>
                        {values?.is_unlockable?.includes("true") &&
                          `Se desbloquea después de ${values?.number_to_unlock} ${translateUnits(values?.number_to_unlock, values?.unit_to_unlock)}`}
                      </PewviewDetailsDescription>
                    </PreviewDetail>

                    <div style={{ height: 16 }} />

                    {/* <PreviewDetail style={{ flexDirection: isMobile ? "column" : "row" }}>
                      <PreviewDetailTitle>Visibilidad:</PreviewDetailTitle>
                      <PewviewDetailsDescription style={{ color: "#3C7E44" }}>
                        {values?.show_before_unlock?.includes("true")
                          ? "Visible antes de desbloquear"
                          : "Oculto hasta ser desbloqueado"}
                      </PewviewDetailsDescription>
                    </PreviewDetail> */}

                    {/* <div style={{ height: 16 }} /> */}

                    <PreviewDetail>
                      <PreviewDetailTitle>Al cancelar suscripción:</PreviewDetailTitle>
                      <PewviewDetailsDescription style={{ color: "#717171" }}>
                        {gracePeriodText}
                      </PewviewDetailsDescription>
                    </PreviewDetail>

                    <div style={{ height: 16 }} />

                    <PreviewDetail>
                      <PreviewDetailTitle>
                        Mensaje de desbloqueo:
                      </PreviewDetailTitle>
                      <PewviewDetailsDescription
                        style={{
                          backgroundColor: "#F2FDF5",
                          color: "#3C7E44",
                          padding: "10px",
                        }}
                      >
                        {values?.unlock_message}
                      </PewviewDetailsDescription>
                    </PreviewDetail>
                  </div>
                </Flex>
              </PreviewContainer>
            ) : (
              <PreviewContainer>
                <Flex style={{ alignItems: "flex-start", gap: 10 }}>
                  <Circle>
                    <Icon
                      icon={padlockUnlockIcon}
                      size={"15px"}
                      color="#3C7E44"
                    />
                  </Circle>

                  <div style={{ width: "100%" }}>
                    <PreviewDetail>
                      <PreviewDetailTitle>{values?.name}</PreviewDetailTitle>
                      <PewviewDetailsDescription style={{ color: "#717171" }}>
                        Siempre disponible
                      </PewviewDetailsDescription>
                    </PreviewDetail>
                  </div>
                </Flex>
              </PreviewContainer>
            )}
          </Container>

          <div style={{ height: 20 }} />
        </TabsContent>
      </Tabs>

      <Flex
        style={{
          padding: "5px 18px",
          borderTop: "1px solid rgb(232, 232, 232)",
        }}
      >
        {currentStep === 1 && (
          <Button
            disabled={loading}
            options={{
              type: "outline",
              size: "lg",
              skin: "black",
            }}
            style={{ minWidth: 0 }}
            onClick={() => onClose()}
          >
            Cancelar
          </Button>
        )}

        {currentStep > 1 && (
          <Button
            disabled={loading}
            options={{
              type: "outline",
              size: "lg",
              skin: "black",
            }}
            style={{ minWidth: 0 }}
            onClick={() => prev()}
          >
            Anterior
          </Button>
        )}

        {currentStep < totalSteps && (
          <Button
            disabled={loading}
            options={{
              type: "filled",
              size: "lg",
              skin: "black",
            }}
            style={{ minWidth: 0 }}
            onClick={() => next()}
          >
            <Flex style={{ gap: 8 }}>
              Siguiente
              <Icon icon={arrowFowardIcon} size={"15px"} color="white" />
            </Flex>
          </Button>
        )}

        {currentStep >= totalSteps && (
          <Submit
            isSubmmiting={loading}
            options={{
              type: "filled",
              size: "lg",
              skin: "purple",
            }}
            form={formId}
          >
            {formId === "update-category-form"
              ? "Guardar cambios"
              : "Crear categoría"}
          </Submit>
        )}
      </Flex>
    </>
  );
};

export default CategoryFormComponent;
